import type { ReactNode } from "react";
import { fromJS } from "immutable";
import { connect } from "react-redux";
import { bindActionCreators, type Dispatch } from "redux";
import { Field, change } from "redux-form/immutable";
import {
  injectIntl,
  type IntlShape,
  type WrappedComponentProps
} from "react-intl";
import styled from "styled-components";

import SelectBox from "../select-box";
import type {
  SelectBoxOption,
  SelectBoxValue
} from "../select-box";
import UpRow from "../up-row";

import URL from "./filters/url";
import Referrer from "./filters/referrer";
import ShoppingCartValue from "./filters/shopping-cart-value";
import Element from "./filters/element";
import TimeOfDay from "./filters/time-of-day";
import UptainQuery from "./filters/uptain-query";
import Condition from "./filters/condition";
import CustomerGroup from "./filters/customer-group";
import Revenue from "./filters/revenue";
import NumberOfVisits from "./filters/number-of-visits";
import Language from "./filters/language";
import QueryParameter from "./filters/query-parameter";
import type { ConditionProduct } from "./utils";

const InputGroup = styled.div`
  margin-right: 10px;
`;

type ChangeCondition = (
  form: string,
  field: string,
  value: unknown
) => unknown;

interface CategorySelectFieldProps {
  customProps: {
    member: string;
    options: readonly SelectBoxOption[];
  };
  input: {
    name: string;
    value?: SelectBoxValue;
  };
  meta: {
    form: string;
  };
  change: ChangeCondition;
}

const selectBox = (props: CategorySelectFieldProps) => (
  <InputGroup>
    <SelectBox
      options={props.customProps.options}
      value={props.input.value}
      name={props.input.name}
      onChange={(_name, value) =>
        props.change(
          props.meta.form,
          props.customProps.member,
          fromJS({
            category: value,
            filter: filter[value]
          }),
        )
      }
      minWidth="184px"
      inputDisabled
    />
  </InputGroup>
);

type ConditionCategory =
  | "url"
  | "shoppingCartValue"
  | "referrer"
  | "condition"
  | "customerGroup"
  | "revenue"
  | "numberOfVisits"
  | "timeOfDay"
  | "element"
  | "uptainQuery"
  | "queryParameter"
  | "language";

export const productConditionMap: Record<
  ConditionProduct,
  readonly ConditionCategory[]
> = {
  mail: [
    'url',
    'shoppingCartValue',
    'referrer',
    'condition',
    'customerGroup',
    'revenue',
  ],
  activation: [
    'url',
    'shoppingCartValue',
    'timeOfDay',
    'element',
    'referrer',
    'uptainQuery',
    'condition',
    'customerGroup',
    'queryParameter',
    'revenue',
    'numberOfVisits',
    'language',
  ],
  optin: ["url", "shoppingCartValue", "revenue"]
};

type DefaultConditionFilter = Record<string, unknown>;

const filter: Partial<Record<SelectBoxValue, DefaultConditionFilter>> = {
  url: { operator: "contains", attribute: [] },
  referrer: { operator: "contains", attribute: [] },
  queryParameter: { parameter: "", operator: "contains", attribute: [] },
  shoppingCartValue: { operator: "smaller-or-equals", attribute: "" },
  element: { element: "", operator: "exists", attribute: [] },
  timeOfDay: { operator: "within", element: "0" },
  uptainQuery: { parameter: "", operator: "empty", attribute: [] },
  condition: {},
  customerGroup: { operator: "contains", attribute: [] },
  revenue: { operator: "greater", attribute: "" },
  numberOfVisits: { operator: "greater-or-equals", attribute: "" },
  language: { operator: "oneOf", attribute: [] }
};

const options = (
  intl: IntlShape
): Record<ConditionCategory, SelectBoxOption> => ({
  url: { value: "url", label: "URL" },
  shoppingCartValue: {
    value: 'shoppingCartValue',
    label: intl.formatMessage({
      id: 'components.ConditionLine.shoppingCartValue',
      defaultMessage: 'Shopping Cart Value',
    }),
  },
  referrer: { value: "referrer", label: "Referrer" },
  condition: {
    value: 'condition',
    label: intl.formatMessage({
      id: 'components.ConditionLine.condition',
      defaultMessage: 'Condition',
    }),
  },
  customerGroup: {
    value: 'customerGroup',
    label: intl.formatMessage({
      id: 'components.ConditionLine.customerGroup',
      defaultMessage: 'Customer Group',
    }),
  },
  revenue: {
    value: 'revenue',
    label: intl.formatMessage({
      id: 'components.ConditionLine.revenue',
      defaultMessage: 'Revenue',
    }),
  },
  numberOfVisits: {
    value: 'numberOfVisits',
    label: intl.formatMessage({
      id: 'components.ConditionLine.numberOfVisits',
      defaultMessage: 'Number of visits',
    }),
  },
  timeOfDay: {
    value: 'timeOfDay',
    label: intl.formatMessage({
      id: 'components.ConditionLine.daysTimes',
      defaultMessage: 'Days/Times',
    }),
  },
  element: { value: "element", label: "HTML Element" },
  uptainQuery: { value: "uptainQuery", label: "uptain-query" },
  queryParameter: { value: "queryParameter", label: "URL Parameter" },
  language: {
    value: 'language',
    label: intl.formatMessage({
      id: 'components.ConditionLine.language',
      defaultMessage: 'Language',
    }),
  },
});

interface ConditionValues {
  getIn(path: readonly (string | number)[]): unknown;
}

interface ConditionFields {
  length: number;
  map<Result>(
    callback: (member: string, index: number) => Result
  ): Result[];
  remove(index: number): void;
}

interface ConditionLineOwnProps {
  fields: ConditionFields;
  meta: {
    form: string;
  };
  product: ConditionProduct;
  noConditionMessage: ReactNode;
}

interface ConditionLineStateProps {
  myValue: ConditionValues;
  selectedShop?: string;
}

interface ConditionLineDispatchProps {
  change: ChangeCondition;
}

type ConditionLineProps = ConditionLineOwnProps &
  ConditionLineStateProps &
  ConditionLineDispatchProps &
  WrappedComponentProps;

const isConditionCategory = (value: unknown): value is ConditionCategory =>
  typeof value === "string" &&
  Object.prototype.hasOwnProperty.call(filter, value);

const ConditionLine = (props: ConditionLineProps) => {
  const categoryState = (condition: unknown, member: string): ReactNode => {
    if (!isConditionCategory(condition)) return undefined;

    return {
      url: <URL member={member} conditions={props.myValue} />,
      shoppingCartValue: <ShoppingCartValue member={member} />,
      referrer: (
        <Referrer
          member={member}
          product={props.product}
          resetOnChange={() =>
            props.change(
              props.meta.form,
              `${member}.filter.attribute`,
              fromJS([]),
            )
          }
        />
      ),
      condition: <Condition member={member} product={props.product} />,
      customerGroup: (
        <CustomerGroup
          member={member}
          resetOnChange={() =>
            props.change(
              props.meta.form,
              `${member}.filter.attribute`,
              fromJS([]),
            )
          }
        />
      ),
      revenue: <Revenue member={member} product={props.product} />,
      numberOfVisits: <NumberOfVisits member={member} />,
      element: (
        <Element
          member={member}
          resetOnChange={() =>
            props.change(
              props.meta.form,
              `${member}.filter.attribute`,
              fromJS([]),
            )
          }
        />
      ),
      timeOfDay: (
        <TimeOfDay member={member} selectedShop={props.selectedShop} />
      ),
      queryParameter: (
        <QueryParameter
          member={member}
          product={props.product}
          resetOnChange={() =>
            props.change(
              props.meta.form,
              `${member}.filter.attribute`,
              fromJS([]),
            )
          }
        />
      ),
      language: (
        <Language
          member={member}
          resetOnChange={() =>
            props.change(
              props.meta.form,
              `${member}.filter.attribute`,
              fromJS([]),
            )
          }
        />
      ),
      uptainQuery: (
        <UptainQuery
          member={member}
          resetOnChange={() =>
            props.change(
              props.meta.form,
              `${member}.filter.attribute`,
              fromJS([]),
            )
          }
        />
      ),
    }[condition];
  };

  return (
    <div>
      {props.fields.length === 0 ? (
        <div>
          {/* No conditions set: The algorithm has the permission to show this dialog box everytime. If you want to define
          rules when and where this dialog box should be visible add conditions. */}
          {props.noConditionMessage}
        </div>
      ) : (
        <div>
          {props.fields.map((member, index) => (
            <UpRow
              key={member}
              remove={() => props.fields.remove(index)}
              breakpoint="550px"
            >
              <Field
                name={`${member}.category`}
                component={selectBox}
                customProps={{
                  options: productConditionMap[props.product].map(
                    (option) => options(props.intl)[option],
                  ),
                  member,
                }}
                change={props.change}
              />
              {categoryState(
                props.myValue.getIn([index, 'category']),
                member,
              )}
            </UpRow>
          ))}
        </div>
      )}
    </div>
  );
};

// REDUX
interface LegacyConditionLineState {
  getIn(
    path: readonly ["form", string, "values", "conditions"]
  ): ConditionValues;
  getIn(
    path: readonly [
      "customerBackendDashboard",
      "userData",
      "selectedShop"
    ]
  ): string | undefined;
}

type ConnectedOwnProps = ConditionLineOwnProps & WrappedComponentProps;

const mapStateToProps = (
  state: LegacyConditionLineState,
  ownProps: ConnectedOwnProps
): ConditionLineStateProps => ({
  myValue: state.getIn([
    "form",
    ownProps.meta.form,
    "values",
    "conditions"
  ]),
  selectedShop: state.getIn([
    "customerBackendDashboard",
    "userData",
    "selectedShop"
  ])
});

const matchDispatchToProps = (dispatch: Dispatch): ConditionLineDispatchProps =>
  bindActionCreators({ change }, dispatch);

const ConnectedConditionLine = connect<
  ConditionLineStateProps,
  ConditionLineDispatchProps,
  ConnectedOwnProps,
  LegacyConditionLineState
>(mapStateToProps, matchDispatchToProps)(ConditionLine);

export default injectIntl(ConnectedConditionLine);
